import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  GetResourceSearchResultsAPIResponse,
  GetResourceSearchResultsItem,
  GetResourceSearchResultsRequest,
  ResourceTypeFacet,
} from "@/api/getResourceSearchResults/types";
import { AppDispatch, AppThunk } from "@/app/store";
import { getResourceSearchResults } from "@/api/getResourceSearchResults/getResourceSearchResultsApi";
import {
  Trade,
  BusinessRole,
} from "@/modules/hub/features/ResourceLibrary/api/getResourceLibratyMetadata/types";
import { ResourceCategory } from "@/modules/hub/features/ResourceLibrary/api/getResourceLibratyMetadata/types";

interface ResourceLibraryState {
  resources: GetResourceSearchResultsItem[];
  resourceTypes: ResourceTypeFacet[];
  page: number;
  hasMore: boolean;
  totalCount: number;
  searchInput: string;
  showOnlyFavorites: boolean;
  showOnlyFeatured: boolean;
  selectedResourceType: number | null;
  selectedTag: string | null;
  scrollPosition: number;
  isRestoringState: boolean;
  isFetching: boolean;
  pendingRequest: "search" | "pagination" | null;
  lastFetchParams: {
    searchTerm?: string;
    showFavorites?: boolean;
    showFeatured?: boolean;
    resourceTypeIds?: number[];
    tradeIds?: number[];
    resourceCategoryIds?: number[];
    businessRoleIds?: number[];
    page?: number;
    pageSize?: number;
  } | null;

  selectedResourceTypes: ResourceTypeFacet[];
  selectedTrades: Trade[];
  selectedResourceCategories: ResourceCategory[];
  selectedBusinessRoles: BusinessRole[];
}

const initialState: ResourceLibraryState = {
  resources: [],
  resourceTypes: [],
  page: 1,
  hasMore: true,
  totalCount: 0,
  searchInput: "",
  showOnlyFavorites: false,
  showOnlyFeatured: false,
  selectedResourceType: null,
  selectedTag: null,
  scrollPosition: 0,
  isRestoringState: false,
  isFetching: false,
  pendingRequest: null,
  lastFetchParams: null,
  selectedResourceTypes: [],
  selectedTrades: [],
  selectedResourceCategories: [],
  selectedBusinessRoles: [],
};

const resourceLibrarySlice = createSlice({
  name: "resourceLibrary",
  initialState,
  reducers: {
    setResources: (
      state,
      action: PayloadAction<GetResourceSearchResultsItem[]>,
    ) => {
      state.resources = action.payload;
    },
    appendResources: (
      state,
      action: PayloadAction<GetResourceSearchResultsItem[]>,
    ) => {
      // Filter out duplicates based on resource ID
      const existingIds = new Set(state.resources.map((r) => r.id));
      const newResources = action.payload.filter((r) => !existingIds.has(r.id));
      state.resources = [...state.resources, ...newResources];
    },
    setResourceTypes: (state, action: PayloadAction<ResourceTypeFacet[]>) => {
      state.resourceTypes = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setHasMore: (state, action: PayloadAction<boolean>) => {
      state.hasMore = action.payload;
    },
    setTotalCount: (state, action: PayloadAction<number>) => {
      state.totalCount = action.payload;
    },
    setSearchInput: (state, action: PayloadAction<string>) => {
      state.searchInput = action.payload;
    },
    setShowOnlyFavorites: (state, action: PayloadAction<boolean>) => {
      state.showOnlyFavorites = action.payload;
    },
    setShowOnlyFeatured: (state, action: PayloadAction<boolean>) => {
      state.showOnlyFeatured = action.payload;
    },
    setScrollPosition: (state, action: PayloadAction<number>) => {
      state.scrollPosition = action.payload;
    },
    setIsRestoringState: (state, action: PayloadAction<boolean>) => {
      state.isRestoringState = action.payload;
    },
    setLastFetchParams: (
      state,
      action: PayloadAction<ResourceLibraryState["lastFetchParams"]>,
    ) => {
      state.lastFetchParams = action.payload;
    },
    setSelectedResourceTypes: (
      state,
      action: PayloadAction<ResourceTypeFacet[]>,
    ) => {
      state.selectedResourceTypes = action.payload;
    },
    setSelectedTrades: (state, action: PayloadAction<Trade[]>) => {
      state.selectedTrades = action.payload;
    },
    setSelectedResourceCategories: (
      state,
      action: PayloadAction<ResourceCategory[]>,
    ) => {
      state.selectedResourceCategories = action.payload;
    },
    setSelectedBusinessRoles: (
      state,
      action: PayloadAction<BusinessRole[]>,
    ) => {
      state.selectedBusinessRoles = action.payload;
    },
    setIsFetching: (state, action: PayloadAction<boolean>) => {
      state.isFetching = action.payload;
    },
    setPendingRequest: (
      state,
      action: PayloadAction<"search" | "pagination" | null>,
    ) => {
      state.pendingRequest = action.payload;
    },
    resetState: (state) => {
      Object.assign(state, initialState);
    },
    clearFilters: (state) => {
      state.searchInput = "";
      state.showOnlyFavorites = false;
      state.showOnlyFeatured = false;
      state.selectedResourceType = null;
      state.selectedTag = null;
      state.selectedResourceTypes = [];
      state.selectedTrades = [];
      state.selectedResourceCategories = [];
      state.selectedBusinessRoles = [];
      state.totalCount = 0;
    },
    hydrateFromCache: (
      state,
      action: PayloadAction<Partial<ResourceLibraryState>>,
    ) => {
      const cachedState = action.payload;

      if (cachedState.resources) {
        state.resources = cachedState.resources;
      }
      if (cachedState.page) {
        state.page = cachedState.page;
      }
      if (typeof cachedState.hasMore === "boolean") {
        state.hasMore = cachedState.hasMore;
      }
      if (cachedState.scrollPosition) {
        state.scrollPosition = cachedState.scrollPosition;
      }
      if (cachedState.lastFetchParams) {
        state.lastFetchParams = cachedState.lastFetchParams;
      }
      if (cachedState.searchInput !== undefined) {
        state.searchInput = cachedState.searchInput;
      }
      if (typeof cachedState.showOnlyFavorites === "boolean") {
        state.showOnlyFavorites = cachedState.showOnlyFavorites;
      }
      if (typeof cachedState.showOnlyFeatured === "boolean") {
        state.showOnlyFeatured = cachedState.showOnlyFeatured;
      }
      if (cachedState.selectedResourceType !== undefined) {
        state.selectedResourceType = cachedState.selectedResourceType;
      }
      if (typeof cachedState.totalCount === "number") {
        state.totalCount = cachedState.totalCount;
      }
    },
  },
});

// Promise queue to ensure requests run sequentially
let currentRequest: Promise<void> | null = null;

export const fetchResources =
  (params: GetResourceSearchResultsRequest): AppThunk =>
  async (dispatch: AppDispatch, getState) => {
    // Wait for any current request to complete
    if (currentRequest) {
      await currentRequest;
    }

    // If there's a pending pagination request, skip it since we're doing a fresh search
    const state = getState();
    if (state.resourceLibrary.pendingRequest === "pagination") {
      dispatch(setPendingRequest(null));
      return;
    }

    dispatch(setIsFetching(true));

    // Create new promise for this request
    currentRequest = (async () => {
      try {
        const response: GetResourceSearchResultsAPIResponse =
          await getResourceSearchResults(params);
        const data = response.data;

        dispatch(setResources(data.resources || []));
        dispatch(setPage(params.page || 1));
        dispatch(setLastFetchParams(params));

        // Set total count from API response
        if (response.meta?.totalCount !== undefined) {
          dispatch(setTotalCount(response.meta.totalCount));
        }

        const hasMore =
          !!data && data.resources.length >= (params.pageSize || 10);
        dispatch(setHasMore(hasMore));
      } catch (error) {
        console.error("Error fetching resources:", error);
        dispatch(setHasMore(false));
      } finally {
        dispatch(setIsFetching(false));
        currentRequest = null;
      }
    })();

    await currentRequest;
  };

export const fetchMoreResources =
  (params: GetResourceSearchResultsRequest): AppThunk =>
  async (dispatch: AppDispatch, getState) => {
    const state = getState();

    // Don't paginate if we're already fetching
    if (state.resourceLibrary.isFetching) {
      // Queue this as a pending pagination request
      dispatch(setPendingRequest("pagination"));
      return;
    }

    // Wait for any current request to complete
    if (currentRequest) {
      await currentRequest;
    }

    dispatch(setIsFetching(true));
    dispatch(setPendingRequest(null));

    // Create new promise for this request
    currentRequest = (async () => {
      try {
        const response: GetResourceSearchResultsAPIResponse =
          await getResourceSearchResults(params);
        const data = response.data;

        dispatch(appendResources(data.resources || []));
        dispatch(setPage(params.page || 1));
        dispatch(setLastFetchParams(params));

        // Update total count if provided (should remain same for pagination)
        if (response.meta?.totalCount !== undefined) {
          dispatch(setTotalCount(response.meta.totalCount));
        }

        const hasMore =
          !!data && data.resources.length >= (params.pageSize || 10);
        dispatch(setHasMore(hasMore));
      } catch (error) {
        console.error("Error loading more resources:", error);
        dispatch(setHasMore(false));
      } finally {
        dispatch(setIsFetching(false));
        currentRequest = null;

        // Check if there's a pending request after we're done
        const afterState = getState();
        if (afterState.resourceLibrary.pendingRequest === "pagination") {
          // Trigger the pending pagination
          dispatch(setPendingRequest(null));
          const nextPage = afterState.resourceLibrary.page + 1;
          dispatch(fetchMoreResources({ ...params, page: nextPage }));
        }
      }
    })();

    await currentRequest;
  };

export const {
  setResources,
  appendResources,
  setPage,
  setHasMore,
  setTotalCount,
  setSearchInput,
  setShowOnlyFavorites,
  setShowOnlyFeatured,
  setScrollPosition,
  setLastFetchParams,
  setSelectedResourceTypes,
  setSelectedTrades,
  setSelectedResourceCategories,
  setSelectedBusinessRoles,
  setIsRestoringState,
  setIsFetching,
  setPendingRequest,
  clearFilters,
  hydrateFromCache,
} = resourceLibrarySlice.actions;

export default resourceLibrarySlice.reducer;
